import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { buildCreditBreakdown, CreditSegmentView, formatCredits } from "./creditBreakdown";
import { CreditCostBlock } from "./CreditCostBlock";
import { RunScope } from "./runScope";
import { useCreditBalance } from "./useCreditBalance";

export interface EmbeddedCreditCostParams {
	/** Whether the form is open; a closed form fetches no balance. */
	active: boolean;
	/** The leads the form's Apply-to runs on, as the run resolves them. */
	scope: RunScope;
	unitCredits: number;
	/** The copy block under `creditConfirm` that names the rows, e.g. "deepResearch". */
	copy: "deepResearch" | "aiColumn";
}

export interface EmbeddedCreditCost {
	/** The cost block for the form's body, or nothing when the form runs on no lead. */
	block: ReactNode;
	/** Whether the form may run: always when it runs on no lead, else per the balance. */
	canConfirm: boolean;
}

/**
 * The credit-confirm modal's cost block, embedded in a form that runs an action itself (the
 * custom-column Deep Research and AI forms, PREC-4263): no second dialog after Save, and the
 * cost follows the form's Apply-to as it changes. The same breakdown and balance rules as the
 * modal, so a total the balance cannot cover disables the form's Save and offers a top-up. A run
 * whose leads the form cannot count still shows its price per lead, as decided by the run (S1).
 */
export function useEmbeddedCreditCost({
	active,
	scope,
	unitCredits,
	copy,
}: EmbeddedCreditCostParams): EmbeddedCreditCost {
	const { t } = useTranslation();
	const balance = useCreditBalance(active);

	if (scope.kind === "none") {
		return { block: null, canConfirm: true };
	}

	const unit = formatCredits(unitCredits);
	const segment: CreditSegmentView =
		scope.kind === "count"
			? {
					id: "LEADS",
					label: t(`creditConfirm.${copy}.LEADS`, { count: scope.count, unit }),
					count: scope.count,
					unitCredits,
					included: true,
				}
			: {
					id: "LEADS",
					label: t(`creditConfirm.${copy}.LEADS_UNKNOWN`, { unit }),
					count: 0,
					unitCredits,
					included: true,
					variable: true,
				};
	const breakdown = buildCreditBreakdown([segment], balance.balance);

	return {
		block: (
			<div className="mt-5 border-t border-gray-200 pt-5">
				<CreditCostBlock balance={balance} breakdown={breakdown} />
			</div>
		),
		canConfirm: breakdown.canConfirm,
	};
}
